import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import usePageTitle from 'hooks/usePageTitle';
import useEscapeClose from 'hooks/useEscapeClose';
import { formatDate } from '../../../utils/jour';

const STATUT_COLORS = {
  en_service: '#2e7d32',
  en_panne: '#e53935',
  en_maintenance: '#f57c00',
  hors_service: '#9e9e9e',
};
const STATUT_LABELS = {
  en_service: 'En service',
  en_panne: 'En panne',
  en_maintenance: 'En maintenance',
  hors_service: 'Hors service',
};
const TYPES = ['voiture', 'moto', 'pickup', 'utilitaire'];
const CARBURANTS = ['essence', 'diesel', 'hybride'];
const STATUTS = ['en_service', 'en_panne', 'en_maintenance', 'hors_service'];

const EMPTY = {
  immatriculation: '', marque: '', modele: '', type: 'voiture', annee: '',
  couleur: '', assurance_numero: '', assurance_expiration: '',
  visite_technique_expiration: '', kilometrage: '', carburant: 'diesel',
  statut: 'en_service', notes: '',
};

function isExpiringSoon(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (d - now) / (1000 * 60 * 60 * 24);
  return diff <= 30;
}

function isExpired(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

/* ⚠️ LA DEFINITION LOCALE A ETE RETIREE LE 16/10/2026. `new Date(dateStr)` sur
 *    une date sans heure est interprete en UTC par la norme : affiche avec
 *    `toLocaleDateString` dans un fuseau NEGATIF, il rendait la veille.
 *    `utils/jour.js#formatDate` DECOUPE 'AAAA-MM-JJ' et reassemble — aucun
 *    instant n'est construit, donc aucun fuseau n'intervient. */

export default function Vehicules() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [error, setError] = useState('');

  useEscapeClose(showForm, () => setShowForm(false));

  const alertCount = (stats.assurances_a_renouveler || 0) + (stats.visites_a_renouveler || 0);
  usePageTitle('Vehicules', alertCount);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType) params.type = filterType;
      if (filterStatut) params.statut = filterStatut;
      const [listRes, statsRes] = await Promise.all([
        api.get('/vehicules', { params }),
        api.get('/vehicules/stats'),
      ]);
      setItems(listRes.data);
      setStats(statsRes.data);
    } catch {} finally { setLoading(false); }
  }, [filterType, filterStatut]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY);
    setError('');
    setShowForm(true);
  };

  const openEdit = (v) => {
    setEditId(v.id);
    setForm({
      immatriculation: v.immatriculation || '',
      marque: v.marque || '',
      modele: v.modele || '',
      type: v.type || 'voiture',
      annee: v.annee || '',
      couleur: v.couleur || '',
      assurance_numero: v.assurance_numero || '',
      assurance_expiration: v.assurance_expiration ? v.assurance_expiration.split('T')[0] : '',
      visite_technique_expiration: v.visite_technique_expiration ? v.visite_technique_expiration.split('T')[0] : '',
      kilometrage: v.kilometrage || '',
      carburant: v.carburant || 'diesel',
      statut: v.statut || 'en_service',
      notes: v.notes || '',
    });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editId) {
        await api.put(`/vehicules/${editId}`, form);
      } else {
        await api.post('/vehicules', form);
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditId(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce vehicule ?')) return;
    try {
      await api.delete(`/vehicules/${id}`);
      load();
    } catch {}
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const statCards = [
    { label: 'Total', value: stats.total || 0, color: '#1565c0' },
    { label: 'En service', value: stats.en_service || 0, color: '#2e7d32' },
    { label: 'En panne', value: stats.en_panne || 0, color: '#e53935' },
    { label: 'Assurances a renouveler', value: stats.assurances_a_renouveler || 0, color: '#f57c00' },
  ];

  return (
    <div>
{/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {statCards.map(sc => (
          <div key={sc.label} className="content-card" style={{ padding: '1.2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: sc.color }}>{sc.value}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--gss-text-light)', marginTop: '0.25rem' }}>{sc.label}</div>
          </div>
        ))}
      </div>

      {/* Alerts */}
      {alertCount > 0 && (
        <div className="content-card" style={{ padding: '0.8rem 1.2rem', marginBottom: '1rem', borderLeft: '4px solid #f57c00', background: '#fff8e1' }}>
          <strong style={{ color: '#e65100' }}>Attention :</strong>{' '}
          {stats.assurances_a_renouveler > 0 && <span>{stats.assurances_a_renouveler} assurance(s) expirent dans les 30 jours. </span>}
          {stats.visites_a_renouveler > 0 && <span>{stats.visites_a_renouveler} visite(s) technique(s) expirent dans les 30 jours.</span>}
        </div>
      )}

      <div className="content-card">
        <div className="gss-toolbar">
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <select className="gss-input" style={{ width: 'auto', minWidth: '140px' }} value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">Tous les types</option>
              {TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
            <select className="gss-input" style={{ width: 'auto', minWidth: '150px' }} value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
              <option value="">Tous les statuts</option>
              {STATUTS.map(s => <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
            </select>
            <span style={{ color: 'var(--gss-text-light)', fontSize: '0.85rem' }}>{items.length} vehicule(s)</span>
          </div>
          <button className="gss-btn gss-btn-primary" onClick={openCreate}>+ Nouveau vehicule</button>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
        ) : items.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDDDDD" strokeWidth="1.5" style={{ marginBottom: 16 }}>
              <path d="M5 17h14M5 17a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2M5 17l-1 4h1m14-4l1 4h-1"/>
            </svg>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#666', marginBottom: 4 }}>Aucun vehicule enregistre</div>
            <div style={{ fontSize: 14, color: '#999' }}>Cliquez sur le bouton pour commencer</div>
          </div>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr>
                  <th>Immatriculation</th>
                  <th>Marque / Modele</th>
                  <th>Type</th>
                  <th>Statut</th>
                  <th>Kilometrage</th>
                  <th>Assurance</th>
                  <th>Visite technique</th>
                  <th style={{ width: 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(v => (
                  <tr key={v.id}>
                    <td><strong>{v.immatriculation}</strong></td>
                    <td>{[v.marque, v.modele].filter(Boolean).join(' ') || '-'}{v.annee ? ` (${v.annee})` : ''}</td>
                    <td>{v.type ? v.type.charAt(0).toUpperCase() + v.type.slice(1) : '-'}</td>
                    <td>
                      <span className="gss-badge" style={{
                        background: (STATUT_COLORS[v.statut] || '#999') + '20',
                        color: STATUT_COLORS[v.statut] || '#999',
                      }}>
                        {STATUT_LABELS[v.statut] || v.statut}
                      </span>
                    </td>
                    <td>{v.kilometrage != null ? Number(v.kilometrage).toLocaleString() + ' km' : '-'}</td>
                    <td>
                      {v.assurance_expiration ? (
                        <span style={{ color: isExpired(v.assurance_expiration) ? '#e53935' : isExpiringSoon(v.assurance_expiration) ? '#f57c00' : 'inherit', fontWeight: isExpiringSoon(v.assurance_expiration) ? 600 : 'normal' }}>
                          {formatDate(v.assurance_expiration)}
                          {isExpired(v.assurance_expiration) && ' (expiree)'}
                          {!isExpired(v.assurance_expiration) && isExpiringSoon(v.assurance_expiration) && ' (bientot)'}
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      {v.visite_technique_expiration ? (
                        <span style={{ color: isExpired(v.visite_technique_expiration) ? '#e53935' : isExpiringSoon(v.visite_technique_expiration) ? '#f57c00' : 'inherit', fontWeight: isExpiringSoon(v.visite_technique_expiration) ? 600 : 'normal' }}>
                          {formatDate(v.visite_technique_expiration)}
                          {isExpired(v.visite_technique_expiration) && ' (expiree)'}
                          {!isExpired(v.visite_technique_expiration) && isExpiringSoon(v.visite_technique_expiration) && ' (bientot)'}
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      <div className="gss-actions">
                        <button className="gss-btn-icon" onClick={() => openEdit(v)} title="Modifier">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="gss-btn-icon gss-btn-icon--delete" onClick={() => handleDelete(v.id)} title="Archiver">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal creation/edition */}
      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                <h2>{editId ? 'Modifier le vehicule' : 'Nouveau vehicule'}</h2>
              </div>
              <div className="gss-modal-subtitle">Gestion du parc automobile</div>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            {error && <div style={{ padding: '0.5rem 1.5rem', color: '#e53935', fontSize: '0.9rem' }}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                {/* Section: Identification */}
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                  <span>IDENTIFICATION DU VEHICULE</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Immatriculation *</label>
                    <input className="gss-input" required value={form.immatriculation} onChange={e => setField('immatriculation', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Marque</label>
                    <input className="gss-input" value={form.marque} onChange={e => setField('marque', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Modele</label>
                    <input className="gss-input" value={form.modele} onChange={e => setField('modele', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Type</label>
                    <select className="gss-input" value={form.type} onChange={e => setField('type', e.target.value)}>
                      {TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                    </select>
                  </div>
                  <div className="gss-field">
                    <label>Annee</label>
                    <input className="gss-input" type="number" min="1990" max="2030" value={form.annee} onChange={e => setField('annee', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Couleur</label>
                    <input className="gss-input" value={form.couleur} onChange={e => setField('couleur', e.target.value)} />
                  </div>
                </div>

                {/* Section: Technique */}
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1.08-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1.08 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001.08 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1.08z"/>
                  </svg>
                  <span>TECHNIQUE & ETAT</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Carburant</label>
                    <select className="gss-input" value={form.carburant} onChange={e => setField('carburant', e.target.value)}>
                      {CARBURANTS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                    </select>
                  </div>
                  <div className="gss-field">
                    <label>Kilometrage</label>
                    <input className="gss-input" type="number" min="0" value={form.kilometrage} onChange={e => setField('kilometrage', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Statut</label>
                    <select className="gss-input" value={form.statut} onChange={e => setField('statut', e.target.value)}>
                      {STATUTS.map(s => <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
                    </select>
                  </div>
                </div>

                {/* Section: Documents */}
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <span>DOCUMENTS & ECHEANCES</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>N. Assurance</label>
                    <input className="gss-input" value={form.assurance_numero} onChange={e => setField('assurance_numero', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Expiration assurance</label>
                    <input className="gss-input" type="date" value={form.assurance_expiration} onChange={e => setField('assurance_expiration', e.target.value)} />
                  </div>
                  <div className="gss-field">
                    <label>Expiration visite technique</label>
                    <input className="gss-input" type="date" value={form.visite_technique_expiration} onChange={e => setField('visite_technique_expiration', e.target.value)} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label>Notes</label>
                    <textarea className="gss-input" rows="3" value={form.notes} onChange={e => setField('notes', e.target.value)} style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>
                  {saving ? 'Enregistrement...' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg>{editId ? 'Modifier' : 'Creer'}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
